import classNames from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  postMessage,
  postUpdateUnreadStatus,
} from '../../actions/communication';
import { errorMessage } from '../../actions/internal';
import { useConversationsContext } from '../../contexts/ConversationsContext';
import { useConversation } from '../../hooks/useConversations';
import { useMessageChannel } from '../../hooks/useMessageChannel';
import { LoadingSpinner } from '../helpers/LoadingSpinner';
import MessagesPanel from './MessagesPanel';

const COLLAPSE_THRESHOLD = 120;

const formatMessageTime = (date) => {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDayMarker = (date, t) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return t('communication.today');
  if (isSameDay(date, yesterday)) return t('communication.yesterday');

  const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'long' });
  }
  if (date.getFullYear() === today.getFullYear()) {
    return date.toLocaleDateString([], { month: 'long', day: 'numeric' });
  }
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
};

const CollapsibleMessageBody = ({ children, t }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const check = () => {
      if (el.scrollHeight > COLLAPSE_THRESHOLD) setNeedsCollapse(true);
    };

    check();

    // Re-check after images/media load
    const images = el.querySelectorAll('img');
    images.forEach(img => img.addEventListener('load', check));

    const observer = new ResizeObserver(check);
    observer.observe(el);

    return () => {
      images.forEach(img => img.removeEventListener('load', check));
      observer.disconnect();
    };
  }, [children]);

  return (
    <div>
      <div
        ref={bodyRef}
        className={needsCollapse && !expanded ? 'o-communication__chat-panel-message__body--collapsed' : ''}
      >
        {children}
      </div>
      {needsCollapse && (
        <button
          className="o-communication__chat-panel-message__show-more"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? t('communication.showLess') : t('communication.showMore')}
        </button>
      )}
    </div>
  );
};

const ChatPanel = ({
  auth,
  authorId,
  errorMessage,
  postMessage,
  postUpdateUnreadStatus,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const { conversationsData, updateConversationsData } = useConversationsContext();
  const playerId = conversationsData?.selectedConversationId || null;

  const {
    data,
    isLoading,
    error,
    isError,
    invalidateConversation,
  } = useConversation(playerId)

  const handleNewMessage = (message) => {
    const conversationId = message.sourceMessage?.messagingChannelId || message.messagingChannelId;

    if (conversationId) {
      postUpdateUnreadStatus(conversationId);
    }

    scrollToBottom();
  };

  useMessageChannel(playerId, handleNewMessage);

  useEffect(() => {
    if (isError) {
      errorMessage(t('errors.generic'));
      console.error('Error loading conversations:', error);
    }
  }, [isError, error]);

  const prevPlayerIdRef = useRef(null);
  useEffect(() => {
    if (!isLoading && data?.messages && playerId !== prevPlayerIdRef.current) {
      prevPlayerIdRef.current = playerId;
      scrollToBottom();
    }
  }, [playerId, isLoading, data?.messages]);

  useEffect(() => {
    if (!isLoading && data?.conversation) {
      postUpdateUnreadStatus(data?.conversation?.id);
      updateConversationsData({ conversationInfo: data.conversation });
    }
  }, [isLoading, data?.conversation, postUpdateUnreadStatus]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  const renderPlaceholder = () => {
    return (<div className="text-center">{t('communication.selectConversation')}</div>)
  }

  const isSystemMessage = (message) => {
    return message.sourceMessage?.messageKlass === 'SystemMessage';
  }

  const isFromAdminPanel = (message) => {
    return message.senderType === 'AdminUser' && !isOwn(message);
  }

  const isOwn = (message) => {
    return message.sourceMessage?.creator?.id === authorId;
  }

  const author = (message) => {
    return message.sender?.senderName || message.sender?.name || message.creator?.senderName || message.creator?.name;
  }

  const handleSendMessage = async (conversationId, messagePayload) => {
    try {
      await postMessage(conversationId, messagePayload);
      refreshConversation();
    } catch (error) {
      errorMessage(t('communication.failedToSend'));
      console.error('Error sending message:', error);
    }
  }

  const refreshConversation = () => {
    invalidateConversation();
    scrollToBottom();
  }

  const renderChat = () => {
    const renderBadgeTranslations = (message) => {
      const badge = message.rewardBadge;
      const translationObjects = {
        title: badge.titleTranslations,
        message_body: badge.messageBodyTranslations,
        message_from: badge.messageFromTranslations,
      }

      const translations = Object.entries(translationObjects).map(([type, list]) => {
        return Object.entries(list).map(([locale, value]) => {
          if (!value || isEmpty(value)) {
            return null;
          }

          return (
            <div key={`${type}-${locale}`} className="o-communication__chat-panel-message__content--translation">
              <strong>{locale} {type}:</strong> {value}
            </div>
          );
        })
      }).filter(Boolean);

      return(
        <div>
          <hr/>
          {`title: ${badge.title}`}<br/>
          {`message_body: ${badge.messageBody}`}<br/>
          {!isEmpty(translations) ? (<hr/>) : null}
          {translations}
        </div>
      )
    }

    let lastDayKey = null;

    return (
      <div className="o-communication__chat-panel">
        <div>
          {data.messages.map(message => {
            const messageDate = new Date(message.createdAt);
            const dayKey = `${messageDate.getFullYear()}-${messageDate.getMonth()}-${messageDate.getDate()}`;
            const showDayMarker = dayKey !== lastDayKey;
            lastDayKey = dayKey;

            return (
            <React.Fragment key={`message-${message.id}`}>
              {showDayMarker && (
                <div className="o-communication__day-marker">
                  <span>{formatDayMarker(messageDate, t)}</span>
                </div>
              )}
              <div
                 className={classNames('o-communication__chat-panel-message', {
                   'o-communication__chat-panel-message--left': message.senderType === 'Account',
                   'o-communication__chat-panel-message--right': message.senderType !== 'Account',
                   'o-communication__chat-panel-message--system': isSystemMessage(message),
                   'o-communication__chat-panel-message--admin-panel': isFromAdminPanel(message),
                   'o-communication__chat-panel-message--own': isOwn(message),
                 })}>
              <div className="o-communication__chat-panel-message__content">
                <div className="o-communication__chat-panel-message__content--header">
                  <div className="o-communication__chat-panel-message__content--name">
                    {message.messageType === 'badge_announcement' ? message.rewardBadge.messageFrom : author(message)}
                  </div>
                  <div className="o-communication__chat-panel-message__content--date-time u-txt--small-xs">
                    {formatMessageTime(messageDate)}
                  </div>
                </div>
                <CollapsibleMessageBody t={t}>
                  <div className="o-communication__chat-panel-message__content--body">
                    {message.message}
                    {message.messageType === 'badge_announcement' && renderBadgeTranslations(message)}
                  </div>
                  {(!isEmpty(message.cards) || !isEmpty(message.programContentEntries)) && (
                    <div className={classNames('o-communication__chat-panel-message__attachments', {
                      'o-communication__chat-panel-message__attachments--with-text': !!message.message,
                    })}>
                      {message.cards.map((c, idx) =>
                        <div key={`c-${c.id}-${idx}`} className="o-communication__chat-panel-message__attachment o-communication__chat-panel-message__attachment--card">
                          <i className="fa fa-clone" aria-hidden="true"></i>
                          <span>{c.title}</span>
                        </div>
                      )}
                      {message.programContentEntries.map((p, idx) =>
                        <div key={`pc-${p.id}-${idx}`} className="o-communication__chat-panel-message__attachment o-communication__chat-panel-message__attachment--tip">
                          <i className="fa fa-play-circle" aria-hidden="true"></i>
                          <span>{p.title}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {message.messageType === 'badge_announcement' && (
                    <div className="o-communication__chat-panel-message__content--badge">
                      <img src={message.rewardBadge.imageUrl} alt={t('communication.badgeAnnouncement')} />
                    </div>
                  )}
                </CollapsibleMessageBody>
              </div>
            </div>
            </React.Fragment>
          )})}
          <div ref={messagesEndRef}></div>
        </div>
      </div>
    )
  }

  const renderConversation = () => {
    if (isLoading || !data?.conversation) {
      return (<LoadingSpinner/>);
    }

    return (
      <div className="o-communication__chat-container-inner">
        {renderChat()}
        <MessagesPanel
          conversationId={data.conversation.id}
          player={{ ...data.conversation }}
          onSendMessage={handleSendMessage}/>
      </div>
    );
  }

  return (
    <div className="o-communication__chat-container">
      {playerId === null ? renderPlaceholder() : renderConversation()}
    </div>
  )
};

ChatPanel.propTypes = {
  auth: PropTypes.object.isRequired,
  authorId: PropTypes.number.isRequired,
  errorMessage: PropTypes.func.isRequired,
  postMessage: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  errorMessage,
  postMessage,
  postUpdateUnreadStatus,
})(ChatPanel);
